import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Grid,
  Box,
  Typography,
  Tooltip,
  InputAdornment,
  Button,
  Autocomplete,
} from '@mui/material';
import { Edit, Delete, History, Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../../api/axiosConfig';

const fetchDevices = async () => {
  const { data } = await axios.get('/devices');
  return data;
};

const fetchVehicles = async () => {
  const { data } = await axios.get('/vehicles');
  return data;
};

const deleteDevice = async (id) => {
  await axios.delete(`/devices/${id}`);
};

const deviceTypes = ['Все', 'Навигационный прибор', 'ДУТ проводной', 'ДУТ BLE', 'Датчик', 'Камера'];
const deviceStatuses = ['Все', 'Установлен', 'Неисправен', 'В ремонте', 'Снят'];

const DeviceTable = ({ onEdit, onViewHistory }) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    imei: '',
    sim: '',
    vehicle: null,
  });

  const { data: devices, isLoading, error } = useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices,
  });

  const { data: vehicles } = useQuery({
    queryKey: ['vehicles'],
    queryFn: fetchVehicles,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteDevice,
    onSuccess: () => {
      queryClient.invalidateQueries(['devices']);
    },
  });

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this device?')) {
      deleteMutation.mutate(id);
    }
  };

  // Фильтрация данных
  const filteredDevices = useMemo(() => {
    if (!devices) return [];

    return devices.filter(device => {
      if (filters.status && filters.status !== 'Все' && device.status !== filters.status) return false;
      if (filters.type && filters.type !== 'Все' && device.type !== filters.type) return false;
      if (filters.imei && !device.imei?.toLowerCase().includes(filters.imei.toLowerCase())) return false;
      if (filters.sim && !device.sim?.toLowerCase().includes(filters.sim.toLowerCase())) return false;
      if (filters.vehicle) {
        const vehicleId = filters.vehicle._id;
        const deviceVehicleId = device.vehicleId?._id || device.vehicleId;
        if (deviceVehicleId !== vehicleId) return false;
      }
      return true;
    });
  }, [devices, filters]);

  const getStatusColor = (status) => {
    const colors = {
      'Установлен': 'success',
      'Неисправен': 'error',
      'В ремонте': 'warning',
      'Снят': 'default',
    };
    return colors[status] || 'default';
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      type: '',
      imei: '',
      sim: '',
      vehicle: null,
    });
  };

  const hasActiveFilters = filters.status || filters.type || filters.imei || filters.sim || filters.vehicle;

  if (isLoading) return <Typography>Loading devices...</Typography>;
  if (error) return <Typography color="error">Error loading devices: {error.message}</Typography>;

  return (
    <Box>
      {/* Фильтры */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              {deviceStatuses.map((status) => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Type"
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            >
              {deviceTypes.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="IMEI"
              value={filters.imei}
              onChange={(e) => setFilters({ ...filters, imei: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filters.imei && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setFilters({ ...filters, imei: '' })}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              placeholder="Search by IMEI"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="SIM"
              value={filters.sim}
              onChange={(e) => setFilters({ ...filters, sim: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filters.sim && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setFilters({ ...filters, sim: '' })}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              placeholder="Search by SIM"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              fullWidth
              options={vehicles || []}
              getOptionLabel={(option) => `${option.licensePlate} - ${option.brand} ${option.model}`}
              value={filters.vehicle}
              onChange={(event, newValue) => setFilters({ ...filters, vehicle: newValue })}
              renderInput={(params) => (
                <TextField {...params} label="Vehicle" placeholder="Filter by vehicle" />
              )}
              isOptionEqualToValue={(option, value) => option._id === value?._id}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="outlined"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              startIcon={<ClearIcon />}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Found {filteredDevices.length} device(s)
            {hasActiveFilters && ` (filtered from ${devices?.length || 0} total)`}
          </Typography>
        </Box>
      </Paper>

      {/* Таблица */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>IMEI</strong></TableCell>
              <TableCell><strong>SIM</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Model</strong></TableCell>
              <TableCell><strong>Serial Number</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Vehicle</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDevices.map((device) => (
              <TableRow key={device._id} hover>
                <TableCell>{device.imei}</TableCell>
                <TableCell>{device.sim || '-'}</TableCell>
                <TableCell>{device.type}</TableCell>
                <TableCell>{device.model || '-'}</TableCell>
                <TableCell>{device.serialNumber || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={device.status}
                    color={getStatusColor(device.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {device.vehicleId ? device.vehicleId.licensePlate : 'Not installed'}
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit">
                    <IconButton onClick={() => onEdit(device)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton onClick={() => handleDelete(device._id)} color="error">
                      <Delete />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="History">
                    <IconButton onClick={() => onViewHistory(device._id)}>
                      <History />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {filteredDevices.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">No devices found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DeviceTable;